import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import ProofHeader from './components/ProofHeader';
import ProofImageViewer from './components/ProofImageViewer';
import ProofMetadata from './components/ProofMetadata';
import ProofActions from './components/ProofActions';

const ProofDetailView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentLanguage] = useState(localStorage.getItem('language') || 'ko');

  const mockProofs = [
  {
    id: 1,
    proofNumber: 1,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1994e8396-1764671660275.png",
    imageAlt: 'Athletic woman in black sportswear performing plank exercise on yoga mat in bright modern gym with wooden floor',
    submissionDate: '2025-12-25',
    starRating: 5,
    description: '첫 번째 운동 완료! 30분 플랭크 운동을 성공적으로 마쳤습니다.\n오늘은 특히 코어 근육에 집중했고, 자세를 유지하는 것이 어려웠지만 끝까지 완수했습니다.'
  },
  {
    id: 2,
    proofNumber: 2,
    image: "https://images.unsplash.com/photo-1720798198209-f5577ce6aa3e",
    imageAlt: 'Group of diverse people running together on outdoor track during sunrise with mountains in background',
    submissionDate: '2025-12-26',
    starRating: 4,
    description: '두 번째 운동 완료! 5km 러닝을 완주했습니다.\n날씨가 좋아서 야외에서 운동할 수 있었고, 페이스를 잘 유지했습니다.'
  },
  {
    id: 3,
    proofNumber: 3,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1af5cc034-1764671662179.png",
    imageAlt: 'Fit woman in purple sports bra doing dumbbell bicep curls in modern fitness center with mirrored walls',
    submissionDate: '2025-12-27',
    starRating: 5,
    description: '세 번째 운동 완료! 상체 근력 운동에 집중했습니다.\n덤벨 운동과 푸시업을 조합하여 진행했고, 근육통이 느껴지지만 만족스러운 운동이었습니다.'
  },
  {
    id: 4,
    proofNumber: 4,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a127a1a8-1766483711381.png",
    imageAlt: 'Young woman in gray tank top stretching legs on yoga mat in peaceful home studio with plants',
    submissionDate: '2025-12-28',
    starRating: 4,
    description: '네 번째 운동 완료! 요가와 스트레칭 중심의 회복 운동을 했습니다.\n유연성이 조금씩 좋아지는 것을 느낄 수 있었습니다.'
  },
  {
    id: 5,
    proofNumber: 5,
    image: "https://images.unsplash.com/photo-1604335788369-94f349ae5243",
    imageAlt: 'Close-up of hands gripping heavy barbell with weight plates in professional gym setting',
    submissionDate: '2025-12-29',
    starRating: 5,
    description: '다섯 번째 운동 완료! 하체 근력 운동에 집중했습니다.\n스쿼트와 데드리프트를 중심으로 진행했고, 개인 최고 기록을 갱신했습니다!'
  }];


  const [currentProofIndex, setCurrentProofIndex] = useState(0);
  const currentProof = mockProofs?.[currentProofIndex];

  const challengeData = {
    title: '30일 운동 챌린지',
    totalProofs: mockProofs?.length
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.key === 'ArrowLeft' && currentProofIndex > 0) {
        setCurrentProofIndex(currentProofIndex - 1);
      } else if (e?.key === 'ArrowRight' && currentProofIndex < mockProofs?.length - 1) {
        setCurrentProofIndex(currentProofIndex + 1);
      } else if (e?.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentProofIndex, mockProofs?.length]);

  const handlePrevious = () => {
    if (currentProofIndex > 0) {
      setCurrentProofIndex(currentProofIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentProofIndex < mockProofs?.length - 1) {
      setCurrentProofIndex(currentProofIndex + 1);
    }
  };

  const handleEdit = () => {
    navigate('/proof-submission', {
      state: { proofId: currentProof?.id, mode: 'edit' }
    });
  };

  const handleDelete = () => {
    console.log('Deleting proof:', currentProof?.id);
    navigate('/challenge-detail');
  };

  const handleClose = () => {
    navigate('/challenge-detail');
  };

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation />
      <BreadcrumbNavigation />
      <div className="fixed inset-0 z-modal bg-background/95 backdrop-blur-sm pt-16 lg:pt-20 pb-20 lg:pb-8 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div
            className="bg-card rounded-lg shadow-gaming-lg p-4 md:p-6 lg:p-8">

            <ProofHeader
              currentProof={currentProof?.proofNumber}
              totalProofs={challengeData?.totalProofs}
              challengeTitle={challengeData?.title}
              onClose={handleClose} />


            <div className="space-y-6 md:space-y-8">
              <ProofImageViewer
                imageUrl={currentProof?.image}
                imageAlt={currentProof?.imageAlt}
                onPrevious={handlePrevious}
                onNext={handleNext}
                hasPrevious={currentProofIndex > 0}
                hasNext={currentProofIndex < mockProofs?.length - 1} />


              <ProofMetadata
                submissionDate={currentProof?.submissionDate}
                starRating={currentProof?.starRating}
                description={currentProof?.description} />


              <ProofActions onEdit={handleEdit} onDelete={handleDelete} onClose={handleClose} />
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center space-x-2">
            {mockProofs?.map((proof, index) =>
            <button
              key={proof?.id}
              onClick={() => setCurrentProofIndex(index)}
              className={`w-2 h-2 rounded-full transition-gaming ${
              index === currentProofIndex ?
              'bg-primary w-8' : 'bg-muted hover:bg-muted-foreground'}`
              }
              aria-label={`Go to proof ${proof?.proofNumber}`} />

            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProofDetailView;